import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Input from "../../../../../components/common/Input/Input";
import SelectInput from "../../../../../components/common/selectInput/selectInput";
import SingleDatePicker from "../../../../../components/common/SingleDatePicker/SingleDatePicker";
import TelInput from "../../../../../components/common/telInput/telInput";
import useLanguageTranslate from "../../../../../hooks/useLanguageTranslate";
import { updateHelperBasicInfo, selectHelperDetails } from "../../../../../redux/features/domesticHelperFlowSlice";
import {
  daysOptions,
  monthsOptions,
  yearsOptions,
} from "../../../../../utils/calenderOptions";
import DomesticHelperBottomStickyBtnLayout from "../../../layouts/DomesticHelperBottomStickyBtnLayout";
import DomesticHelperJourneyLayout from "../../../layouts/DomesticHelperJourneyLayout";
import "./basicInfo.css";

const BasicInfo = ({ continueClickHandler, onClose }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const translate = useLanguageTranslate();
  const [values, setValues] = useState({});
  const [errors, setErrors] = useState({});
  const [isInitialized, setIsInitialized] = useState(false);

  // Get saved form data from Redux
  const helperDetails = useSelector(selectHelperDetails);
  const savedBasicInfo = helperDetails?.basicInfo;

  // Restore from Redux on mount if data exists
  useEffect(() => {
    // Only initialize once
    if (isInitialized) return;

    if (savedBasicInfo) {
      const rawBirthDate = savedBasicInfo.birthDate || "";
      // A previous bug could persist literal "undefined" strings when partial
      // DOB parts were saved (e.g. "undefined-undefined-05"). Treat those as
      // empty so SelectInput can still match a real option on restore.
      const sanitize = (part) => (!part || part === "undefined" ? "" : part);
      const birthDateParts = rawBirthDate.split("-");
      const safeYear = sanitize(birthDateParts[0]);
      const safeMonth = sanitize(birthDateParts[1]);
      const safeDay = sanitize(birthDateParts[2]);
      setValues({
        fullName: savedBasicInfo.fullName || "",
        email: savedBasicInfo.email || "",
        mobile: savedBasicInfo.contactNumber || "",
        year: safeYear,
        month: safeMonth,
        day: safeDay,
        fullDate: safeYear && safeMonth && safeDay
          ? `${safeYear}-${safeMonth}-${safeDay}`
          : "",
      });
    }
    setIsInitialized(true);
  }, [savedBasicInfo, isInitialized]);


  // Custom validation function with specific error messages
  const validateForm = (data) => {
    const formData = data || values;
    const newErrors = {};

    // Full Name validation
    if (!formData.fullName?.trim()) {
      newErrors.fullName = translate(
        "This field is required.",
        "هذا الحقل مطلوب."
      );
    } else if (!/^[a-zA-Z\u0600-\u06FF\s.]+$/.test(formData.fullName)) {
      newErrors.fullName = translate(
        "Name can only contain alphabetic characters, spaces, and periods.",
        "يجب أن يحتوي الاسم على حروف أبجدية ومسافات ونقاط فقط."
      );
    }

    // Email validation - required first, then format
    if (!formData.email?.trim()) {
      newErrors.email = translate(
        "This field is required.",
        "هذا الحقل مطلوب."
      );
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = translate(
        "Invalid email address.",
        "البريد الإلكتروني غير صحيح."
      );
    }

    // Mobile validation - required first, then length
    if (!formData.mobile?.trim()) {
      newErrors.mobile = translate(
        "This field is required.",
        "هذا الحقل مطلوب."
      );
    } else if (formData.mobile.length !== 8) {
      newErrors.mobile = translate(
        "Invalid mobile number.",
        "رقم الهاتف المحمول غير صحيح."
      );
    }

    // Date of birth validation - check if all fields are provided
    if (!formData.day || !formData.month || !formData.year) {
      newErrors.dateOfBirth = translate(
        "This field is required.",
        "هذا الحقل مطلوب."
      );
    } else {
      // Check if date is in the future
      const selectedDate = new Date(
        parseInt(formData.year, 10),
        parseInt(formData.month, 10) - 1,
        parseInt(formData.day, 10)
      );
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      if (selectedDate > today) {
        newErrors.dateOfBirth = translate(
          "Date of birth cannot be in the future.",
          "لا يمكن أن يكون تاريخ الميلاد في المستقبل."
        );
      } else {
        // Check if user is at least 18 years old
        const age = today.getFullYear() - selectedDate.getFullYear();
        const monthDifference = today.getMonth() - selectedDate.getMonth();

        // Adjust age if birthday hasn't occurred this year yet
        const isBeforeBirthday =
          monthDifference < 0 ||
          (monthDifference === 0 && today.getDate() < selectedDate.getDate());

        const actualAge = isBeforeBirthday ? age - 1 : age;

        if (actualAge < 18) {
          newErrors.dateOfBirth = translate(
            "You must be at least 18 years old.",
            "يجب أن تكون 18 عامًا على الأقل."
          );
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const formSubmitHandler = (e) => {
    e.preventDefault();
    if (validateForm()) {
      // Save basic info to Redux
      const hasFullDob = !!(values.year && values.month && values.day);
      dispatch(
        updateHelperBasicInfo({
          fullName: values.fullName,
          email: values.email,
          contactNumber: values.mobile,
          birthDate: hasFullDob
            ? `${values.year}-${values.month.padStart(2, "0")}-${values.day.padStart(2, "0")}`
            : "",
        })
      );
      continueClickHandler();
    }
  };

  // Helper function to save form data to Redux
  const saveToRedux = (formValues) => {
    const y = formValues.year;
    const m = formValues.month;
    const d = formValues.day;
    // Only persist birthDate when all three parts are real (non-empty, not the
    // literal string "undefined"). Otherwise leave it blank so a later restore
    // never re-hydrates garbage that would clear the SelectInputs.
    const hasFullDob =
      !!y && !!m && !!d && y !== "undefined" && m !== "undefined" && d !== "undefined";
    dispatch(
      updateHelperBasicInfo({
        fullName: formValues.fullName || "",
        email: formValues.email || "",
        contactNumber: formValues.mobile || "",
        birthDate: hasFullDob
          ? `${y}-${m.padStart(2, "0")}-${d.padStart(2, "0")}`
          : "",
      })
    );
  };

  // Mobile date picker handler
  const handleMobileDatePickerSelect = (dateString) => {
    if (!dateString) return;

    // Parse the date string (YYYY-MM-DD format from mobile date picker)
    const [year, month, day] = dateString.split("-");

    const update = {
      ...values,
      day: day,
      month: month,
      year: year,
      fullDate: dateString,
    };

    setValues(update);
    // Only save to Redux after initialization to avoid overwriting restored data
    if (isInitialized) {
      saveToRedux(update);
    }

    // Clear date error when user selects a date
    if (errors?.dateOfBirth) {
      validateForm(update);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    let update = { ...values, [name]: value };
    setValues(update);
    // Only save to Redux after initialization to avoid overwriting restored data
    if (isInitialized) {
      saveToRedux(update);
    }
    if (errors?.[name] || errors?.dateOfBirth) {
      validateForm(update);
    }
  };

  return (
    <div className="basic-info-page w-full">
      <DomesticHelperJourneyLayout
        show_close_btn={false}
        is_hide_back_btn={false}
        isFirstPage={false}
        onClose={onClose}
        customBackHandler={() => navigate("/new-policy")}
      >
        <div className="content min-w-[32%] max767:w-full">
          <h1 className="text-[#1A2024] text-2xl not-italic font-semibold leading-8 tracking-[-0.24px] text-center max767:text-left max767:rtl:text-right">
            {translate(
              "New Domestic Helper Insurance",
              "تأمين العمالة المنزلية"
            )}
          </h1>
          <p className="text-[#1A2024] text-base not-italic font-normal leading-6 tracking-[-0.16px] text-center mt-1 mb-6 max767:text-left max767:rtl:text-right">
            {translate(
              "Welcome! Let's start with some basic information.",
              "مرحبًا! دعنا نبدأ ببعض المعلومات الأساسية."
            )}
          </p>
        </div>
        {/* The form's onSubmit only covers pressing Enter in a field. The
            Continue button is portaled into #dh-footer-portal, which lives
            outside this form in the DOM — and a submit button that is not a
            descendant of its form (and carries no `form` attribute) submits
            nothing, so the click has to be wired explicitly. */}
        <form onSubmit={formSubmitHandler} className="basic-info-form w-full">
          <DomesticHelperBottomStickyBtnLayout
            show_summary={false}
            hide_back_btn={true}
            btn_onClick={formSubmitHandler}
          >
            <div className="w-[32%] max767:w-full mx-auto ipad:w-[60%] !space-y-6">
              <Input
                label={translate("Full Name", "الاسم الكامل")}
                type="text"
                name="fullName"
                value={values.fullName || ""}
                error={errors.fullName}
                handleChange={handleChange}
              />
              <Input
                label={translate("Email Address", "عنوان البريد الإلكتروني")}
                type="email"
                name="email"
                value={values.email || ""}
                error={errors.email}
                handleChange={handleChange}
              />
              <TelInput
                name="mobile"
                value={values.mobile || ""}
                error={errors.mobile}
                onChange={handleChange}
                label={translate("Mobile Number", "رقم الهاتف المحمول")}
                className="mobilenumber px-4 py-3 max767:w-[50%] w-[97%] border-r-0 border-0 rounded-[9px] h-[46px] focus:outline-none"
              />
              <div className="date_of_birth !mt-6">
                <label className="block mb-[5px] text-[#48535B] not-italic font-normal leading-6 text-base max-350:text-[13px]">
                  {translate("Date of Birth", "تاريخ الميلاد")}
                </label>

                {/* Mobile: native date picker (visible <=767px) */}
                <div className="hidden max767:block">
                  <SingleDatePicker
                    value={values.fullDate || ""}
                    onChange={handleMobileDatePickerSelect}
                    placeholder={translate("Select Date", "اختر التاريخ")}
                    error={errors.dateOfBirth}
                    useNativeMobile={true}
                  />
                </div>
                {/* Desktop: select dropdowns (visible >767px) */}
                <div className="block max767:hidden">
                  <div className="flex gap-6 self-stretch">
                    <SelectInput
                      name="day"
                      options_data={daysOptions}
                      formData={values}
                      default_placeholder={translate("Day", "يوم")}
                      handleChange={handleChange}
                      maxMenuHeight={185}
                    />

                    <SelectInput
                      name="month"
                      formData={values}
                      default_placeholder={translate("Month", "شهر")}
                      options_data={monthsOptions}
                      handleChange={handleChange}
                      maxMenuHeight={185}
                    />

                    <SelectInput
                      name="year"
                      formData={values}
                      default_placeholder={translate("Year", "سنة")}
                      options_data={yearsOptions}
                      handleChange={handleChange}
                      maxMenuHeight={185}
                    />
                  </div>
                  {errors.dateOfBirth && (
                    <div className="mt-2 text-red-600 text-sm">
                      {errors.dateOfBirth}
                    </div>
                  )}
                </div>

              </div>
            </div>
          </DomesticHelperBottomStickyBtnLayout>
        </form>
      </DomesticHelperJourneyLayout>
    </div>
  );
};

export default BasicInfo;
